// City and county page configurations. Each entry powers a unique location page
// at /app/frontend/src/pages/CityPage.jsx via slug-based lookup.

export const CITY_PAGES = [
  {
    slug: 'columbus-in-estate-cleanouts',
    city: 'Columbus',
    state: 'Indiana',
    county: 'Bartholomew County',
    service: 'Estate Cleanouts',
    serviceSlug: 'estate-cleanouts',
    h1: 'Estate Cleanouts in Columbus, Indiana',
    metaDescription:
      'Compassionate estate cleanouts in Columbus, Indiana from a woman-owned, MBE/WBE-certified property transition company. Donation-conscious. Photo-based quotes. Call (812) 302-6833.',
    intro:
      "Settling an estate in Columbus is rarely just logistics. It is family. Ohana Property & Transition Services is based right here in Bartholomew County and has helped families across Columbus clear inherited homes, sort heirlooms, route usable goods to local donation partners, and hand off broom-clean properties to realtors and attorneys on schedule. Olga Ortega founded Ohana after a decade in social work because she saw too many Columbus families left to navigate this stage of life alone.",
    localContext: [
      'Coordinated directly with Columbus-area probate attorneys and estate executors',
      'Donations routed through Bartholomew County nonprofits whenever possible',
      'Disposal handled through the Bartholomew County Solid Waste Management District',
      'Realtor-ready handoffs aligned to Columbus listing and showing timelines',
      'Bilingual support available. Olga is a Latina-owned business owner serving Columbus families in English and Spanish',
    ],
    services: [
      'Estate Cleanouts',
      'Senior Downsizing',
      'Inherited Property Clearing',
      'Pre-Listing Property Prep',
      'Donation-Conscious Recovery',
    ],
    faqs: [
      {
        q: 'What is included in an estate cleanout in Columbus, Indiana?',
        a: 'Our Columbus estate cleanouts include complete removal of furniture, appliances, personal property, and debris. We sort items into keep, donate, sell, and discard categories with the family, coordinate donation pickup for usable goods through local Bartholomew County partners, and route everything else through licensed disposal. Final walkthrough is broom-clean and ready to list.',
      },
      {
        q: 'How much does an estate cleanout cost in Columbus, IN?',
        a: 'Pricing in Columbus depends on home size, volume, and access. Most estate cleanouts range from $1,000 for smaller homes to $3,000+ for larger properties with multiple outbuildings. We send a photo-based estimate before scheduling and confirm the final number on-site before starting work.',
      },
      {
        q: 'How fast can you start an estate cleanout in Columbus?',
        a: 'For most Columbus estate cleanouts we can be on-site within 24 to 48 hours of approval. For probate-coordinated jobs we work directly to the executor or attorney timeline so closings and listing dates are not jeopardized.',
      },
      {
        q: 'Do you work with estate attorneys and executors in Columbus?',
        a: 'Yes. Ohana is bonded, insured, and MBE/WBE certified, which makes us a vendor-ready option for Columbus and Bartholomew County estate attorneys. We provide written scope, photo documentation, and donation receipts on request for estate accounting.',
      },
      {
        q: 'What happens to the items removed from a Columbus estate?',
        a: 'Through the Hearth Project, usable furniture, household goods, clothing, and appliances from Columbus estates are first routed to Indiana nonprofits and families in need. Everything else goes to the Bartholomew County Landfill or appropriate recycling channel. Hazardous and prohibited items are flagged before we arrive.',
      },
    ],
  },
  {
    slug: 'columbus-in-junk-removal',
    city: 'Columbus',
    state: 'Indiana',
    county: 'Bartholomew County',
    service: 'Junk Removal',
    serviceSlug: 'junk-removal',
    h1: 'Junk Removal in Columbus, Indiana',
    metaDescription:
      'Same-week junk removal in Columbus, Indiana. Furniture, appliances, garage and basement clutter hauled fast. Woman-owned, MBE/WBE certified. Call (812) 302-6833.',
    intro:
      "Columbus homes accumulate. Garages fill up, basements gather years of stuff, rental turnovers leave a pile in the driveway. Ohana Property & Transition Services is a locally owned junk removal and property transition company based in Columbus, Indiana that handles single-item pickups all the way through full garage and basement cleanouts. We answer the phone, we keep the appointment, and we route usable items to donation through the Hearth Project before anything heads to the Bartholomew County Landfill.",
    localContext: [
      'Bartholomew County disposal handled in-house through licensed channels',
      'Same-week and 24 to 48 hour turnaround for most Columbus addresses',
      'Working appliances routed to donation partners through the Hearth Project',
      'CFC appliance recycling fees passed through at cost. No markup',
      'Columbus-based crew, no franchise overhead',
    ],
    services: [
      'Junk Removal',
      'Furniture Removal',
      'Appliance Removal',
      'Debris Removal',
      'Yard Waste Removal',
    ],
    faqs: [
      {
        q: 'What does junk removal cost in Columbus, Indiana?',
        a: 'Junk removal in Columbus typically runs $100 to $500 depending on volume and access. Single-item pickups start around $75 to $150. Garage and basement cleanouts usually fall between $250 and $700. We send a transparent, photo-based quote before scheduling.',
      },
      {
        q: 'How fast is Columbus junk removal?',
        a: 'Most Columbus jobs are scheduled within 24 to 48 hours of approval. Urgent same-day requests are sometimes possible depending on crew availability. Call (812) 302-6833 to ask.',
      },
      {
        q: 'What can Ohana take in Columbus, IN?',
        a: 'We take furniture, mattresses, appliances (working or not), yard waste, construction debris, household clutter, electronics, garage and basement contents, and renovation waste. The full prohibited-items list (hazardous waste, paint, oils, certain electronics that require special handling) is on our blog.',
      },
      {
        q: 'Do you donate items in Columbus?',
        a: 'Yes. Through the Hearth Project, usable furniture, appliances, and household goods are routed to Bartholomew County nonprofits and families in need before anything is disposed of. Documentation is available on request.',
      },
      {
        q: 'Is Ohana licensed and insured for jobs in Columbus?',
        a: 'Yes. Ohana Property & Transition Services LLC is fully licensed and carries liability insurance for every Columbus job. We are also MBE and WBE certified, which matters for institutional and government-vendor work in Indiana.',
      },
    ],
  },
  {
    slug: 'seymour-in-property-cleanouts',
    city: 'Seymour',
    state: 'Indiana',
    county: 'Jackson County',
    service: 'Property Cleanouts',
    serviceSlug: 'property-cleanouts',
    h1: 'Property Cleanouts in Seymour, Indiana',
    metaDescription:
      'Full property cleanouts in Seymour, Indiana. Estate, rental turnover, and pre-listing cleanouts handled fast. MBE/WBE certified. Call (812) 302-6833.',
    intro:
      'Seymour property cleanouts come with their own rhythm. Rental turnover seasons, inherited family homes, downsizing parents moving closer to grandkids. Ohana Property & Transition Services serves Seymour and Jackson County families, landlords, and realtors with full-property cleanouts, sorted into keep/donate/dispose categories and finished broom-clean. We base out of Columbus, but Seymour is well inside our regular route and we treat every Seymour job like a local one. Because it is.',
    localContext: [
      'Regular Seymour service area. No out-of-town travel surcharge',
      'Coordination with Jackson County disposal and recycling',
      'Bilingual support for Seymour families (English and Spanish)',
      'Rental turnover scheduling that lines up with Seymour leasing cycles',
      'Donations routed locally through Hearth Project partners when possible',
    ],
    services: [
      'Full Property Cleanouts',
      'Estate Cleanouts',
      'Rental Turnover Cleanouts',
      'Pre-Listing Property Prep',
      'Furniture and Appliance Removal',
    ],
    faqs: [
      {
        q: 'Do you serve Seymour, Indiana?',
        a: 'Yes. Seymour is part of our regular service area from our Columbus base. There is no out-of-town surcharge for Seymour, Jackson County, or addresses along US-31 between Columbus and Seymour.',
      },
      {
        q: 'How much does a property cleanout cost in Seymour, IN?',
        a: 'Most Seymour full property cleanouts run $800 to $3,000 depending on home size, volume, and access. We send a photo-based quote before scheduling so the price is locked in before we arrive.',
      },
      {
        q: 'What is included in a Seymour property cleanout?',
        a: 'A full Seymour property cleanout includes interior, garage, attic, basement, and yard items, sorting for donation and disposal, removal of furniture and appliances, and a final broom-clean finish. Carpet removal is available as an add-on.',
      },
      {
        q: 'How fast can you start a Seymour property cleanout?',
        a: 'Most Seymour cleanouts are scheduled within 24 to 48 hours of quote approval. Multi-day projects are scoped from photos so we arrive with the right crew size on day one.',
      },
      {
        q: 'Do you handle rental turnovers in Seymour?',
        a: 'Yes. We work with Seymour landlords and property managers on tenant turnovers. Quick scope, fast scheduling, broom-clean handoff to your maintenance team. Ongoing partnerships welcome.',
      },
    ],
  },
  {
    slug: 'bartholomew-county-junk-removal',
    city: 'Bartholomew County',
    state: 'Indiana',
    county: 'Bartholomew County',
    service: 'Junk Removal',
    serviceSlug: 'junk-removal',
    h1: 'Junk Removal in Bartholomew County, Indiana',
    metaDescription:
      'Bartholomew County, Indiana junk removal and property cleanouts. Columbus-based, locally owned, MBE/WBE certified. Free photo-based quotes. (812) 302-6833.',
    intro:
      "Bartholomew County is home for Ohana Property & Transition Services. We service every town inside the county line. Columbus, Hope, Edinburgh-side, Hartsville, Petersville, Clifford, Taylorsville, and every farm address in between. Junk removal, furniture removal, appliance haul-away, debris removal, and full property cleanouts. Founded by Columbus resident Olga Ortega, Ohana is a woman-owned, Latina-owned business built around showing up when neighbors need help.",
    localContext: [
      'Every address in Bartholomew County is local for us. No travel surcharge',
      'Direct relationships with Bartholomew County Solid Waste Management District',
      'Donation routing through Bartholomew County nonprofit partners',
      'MBE and WBE certified. Institutional and government-vendor ready',
      'Founded and operated by a Columbus resident',
    ],
    services: [
      'Junk Removal',
      'Furniture Removal',
      'Appliance Removal',
      'Estate Cleanouts',
      'Donation-Conscious Recovery',
    ],
    faqs: [
      {
        q: 'What areas of Bartholomew County does Ohana serve?',
        a: 'Every address inside Bartholomew County. Columbus, Hope, Hartsville, Taylorsville, Clifford, Petersville, and all rural addresses in the county. We also serve adjacent counties including Jackson, Decatur, Johnson, Brown, Shelby, and Jennings.',
      },
      {
        q: 'How much does junk removal cost in Bartholomew County?',
        a: 'Standard junk removal in Bartholomew County runs $100 to $500 by volume. Full property cleanouts range from $800 to $3,000+. Every quote is photo-based and confirmed on-site before we start, so there are no surprises.',
      },
      {
        q: 'Is Ohana a Bartholomew County business?',
        a: 'Yes. Ohana Property & Transition Services LLC is based in Columbus, Indiana, founded by Bartholomew County resident Olga Ortega. We are a woman-owned, Latina-owned, MBE and WBE certified company built and operated right here.',
      },
      {
        q: 'Where does Bartholomew County junk go after pickup?',
        a: 'Usable items are first routed to Bartholomew County nonprofit and family donation partners through the Hearth Project. Everything else is taken to the Bartholomew County Landfill or appropriate recycling channel. CFC appliance recycling fees are passed through at cost.',
      },
      {
        q: 'Do you take hazardous items in Bartholomew County?',
        a: 'No. Indiana law and Bartholomew County Solid Waste regulations prohibit standard haulers from taking paint, solvents, oils, batteries, certain electronics, and other hazardous items. We flag those in advance and point you to the BCSWMD Tox-Away facility at 811 E. CR 450 S., Columbus IN.',
      },
    ],
  },
  {
    slug: 'franklin-in-estate-cleanouts',
    city: 'Franklin',
    state: 'Indiana',
    county: 'Johnson County',
    service: 'Estate Cleanouts',
    serviceSlug: 'estate-cleanouts',
    h1: 'Estate Cleanouts in Franklin, Indiana',
    metaDescription:
      'Estate cleanouts in Franklin, Indiana from a Columbus-based, woman-owned property transition company. Compassionate, MBE/WBE certified. Call (812) 302-6833.',
    intro:
      "Franklin families clearing an inherited home or settling a parent's estate deserve a partner who treats the property the way it deserves to be treated. Ohana Property & Transition Services drives north from Columbus to serve Franklin and Johnson County with full estate cleanouts. Sorting belongings into keep, donate, sell, and discard. Routing usable items to Indiana donation partners. Handing the property back to family or realtor broom-clean and ready for the next chapter.",
    localContext: [
      'Regular Franklin and Johnson County route. Columbus-based but Franklin-friendly',
      'Coordinated with Franklin-area realtors and estate attorneys',
      'Donation partners in Johnson County and along the I-65 corridor',
      'MBE and WBE certified. Referral-ready for Franklin estate attorneys',
      'Bilingual support available',
    ],
    services: [
      'Estate Cleanouts',
      'Senior Downsizing',
      'Inherited Property Clearing',
      'Pre-Listing Property Prep',
      'Donation-Conscious Recovery',
    ],
    faqs: [
      {
        q: 'Do you serve Franklin, Indiana?',
        a: 'Yes. Franklin is part of our regular service area along the I-65 corridor between Columbus and Indianapolis. We schedule Franklin estate cleanouts on the same 24 to 48 hour timeline as our Columbus jobs.',
      },
      {
        q: 'How much does an estate cleanout cost in Franklin, IN?',
        a: 'Franklin estate cleanouts typically run $1,000 to $3,000+ depending on home size and contents. Multi-day estates and homes with outbuildings are scoped from photos so the quote is locked in before scheduling.',
      },
      {
        q: 'How does the estate cleanout process work in Franklin?',
        a: 'Send photos and the property address. We respond same day with a scope and price. On the day of the job we walk the property with the family if requested, sort items into keep/donate/sell/dispose, remove everything in the dispose and donate categories, and finish broom-clean.',
      },
      {
        q: 'Do you work with Franklin realtors and estate attorneys?',
        a: 'Yes. Ohana is bonded, insured, and MBE/WBE certified, which makes us referral-ready for Franklin estate attorneys, probate executors, and listing agents. We provide written scope, photos, and donation receipts on request.',
      },
      {
        q: 'What happens to belongings from a Franklin estate?',
        a: 'Usable items are routed through the Hearth Project to Indiana nonprofits and families in need. Hazardous items are flagged before the job. Everything else goes through licensed disposal. We document the disposal pathway for executors who need it.',
      },
    ],
  },
  {
    slug: 'greenwood-in-junk-removal',
    city: 'Greenwood',
    state: 'Indiana',
    county: 'Johnson County',
    service: 'Junk Removal',
    serviceSlug: 'junk-removal',
    h1: 'Junk Removal in Greenwood, Indiana',
    metaDescription:
      'Greenwood, Indiana junk removal and property cleanouts. Columbus-based, woman-owned, MBE/WBE certified. Photo-based quotes, fast turnaround. (812) 302-6833.',
    intro:
      "Greenwood is one of central Indiana's fastest-growing suburbs and that means a lot of moves, a lot of garages getting cleaned out, and a lot of rental turnovers. Ohana Property & Transition Services serves Greenwood with junk removal, furniture and appliance haul-away, garage and basement cleanouts, and rental-turnover support. Woman-owned, MBE and WBE certified, and operated by a team that actually answers the phone.",
    localContext: [
      'Regular Greenwood and Johnson County service area',
      'Same-week and 24 to 48 hour turnaround on most Greenwood jobs',
      'Donation routing through Johnson County partners',
      'Landlord and property manager rates for Greenwood rental turnovers',
      'Photo-based quotes. No in-home estimates required',
    ],
    services: [
      'Junk Removal',
      'Furniture Removal',
      'Appliance Removal',
      'Rental Turnover Cleanouts',
      'Donation-Conscious Recovery',
    ],
    faqs: [
      {
        q: 'How much is junk removal in Greenwood, Indiana?',
        a: 'Greenwood junk removal runs $100 to $500 depending on volume and access. Single-item pickups start around $75 to $150. Whole-room or garage cleanouts typically fall between $250 and $700.',
      },
      {
        q: 'How fast can you come to Greenwood for junk removal?',
        a: 'Most Greenwood jobs schedule within 24 to 48 hours. For urgent same-day or next-day requests, call (812) 302-6833 directly.',
      },
      {
        q: 'Do you serve property managers in Greenwood?',
        a: 'Yes. We work with Greenwood landlords and property managers on rental turnovers with consistent, repeat-friendly pricing. Send the address and turnover date and we will hold the slot.',
      },
      {
        q: 'What can you not take from a Greenwood property?',
        a: 'Hazardous waste, paint, solvents, batteries, certain electronics, and biohazard materials cannot be hauled by standard junk removal in Indiana. We flag those upfront from your photos and point you to the right disposal channel.',
      },
      {
        q: 'Are you insured for jobs in Greenwood?',
        a: 'Yes. Ohana Property & Transition Services LLC carries liability insurance for every Greenwood job. We are also MBE and WBE certified.',
      },
    ],
  },
  {
    slug: 'edinburgh-in-property-cleanouts',
    city: 'Edinburgh',
    state: 'Indiana',
    county: 'Johnson County',
    service: 'Property Cleanouts',
    serviceSlug: 'property-cleanouts',
    h1: 'Property Cleanouts in Edinburgh, Indiana',
    metaDescription:
      'Property cleanouts in Edinburgh, Indiana. Estate, rental turnover, pre-listing. Columbus-based, woman-owned, MBE/WBE certified. Call (812) 302-6833.',
    intro:
      "Edinburgh sits right at the intersection of Bartholomew and Johnson counties, and right in the middle of our regular service area. Ohana Property & Transition Services handles full property cleanouts for Edinburgh families, landlords, realtors, and investors, including estate cleanouts, pre-listing prep, and rental turnovers. Same-day photo quotes, 24 to 48 hour scheduling, and a finish that's ready to list or rent.",
    localContext: [
      'Regular Edinburgh route from our Columbus base',
      'Coordination with both Bartholomew County and Johnson County disposal',
      'Realtor-ready broom-clean handoff',
      'Donation routing through the Hearth Project',
      'Bilingual support. English and Spanish',
    ],
    services: [
      'Full Property Cleanouts',
      'Estate Cleanouts',
      'Pre-Listing Property Prep',
      'Rental Turnover Cleanouts',
      'Furniture and Appliance Removal',
    ],
    faqs: [
      {
        q: 'Do you serve Edinburgh, Indiana?',
        a: 'Yes. Edinburgh is squarely in our regular service area between Columbus and Greenwood. No travel surcharge and same scheduling priority as Columbus addresses.',
      },
      {
        q: 'What is included in an Edinburgh property cleanout?',
        a: 'Edinburgh property cleanouts include interior, garage, attic, basement, and yard, sorted into keep/donate/dispose categories, and finished broom-clean. Carpet removal is available as an add-on.',
      },
      {
        q: 'How much does a property cleanout cost in Edinburgh, IN?',
        a: 'Most Edinburgh property cleanouts run $800 to $3,000 depending on volume and access. We send a photo-based quote before scheduling and confirm the final price on-site before starting work.',
      },
      {
        q: 'How fast can a property cleanout happen in Edinburgh?',
        a: 'Most Edinburgh jobs are scheduled within 24 to 48 hours. Multi-day estates are scoped in advance from photos so we arrive ready to execute on day one.',
      },
      {
        q: 'What happens to items from an Edinburgh property cleanout?',
        a: 'Usable items are routed to Indiana donation partners through the Hearth Project. Everything else is taken to the appropriate licensed disposal facility. Hazardous items are flagged in advance.',
      },
    ],
  },
  {
    slug: 'shelbyville-in-junk-removal',
    city: 'Shelbyville',
    state: 'Indiana',
    county: 'Shelby County',
    service: 'Junk Removal',
    serviceSlug: 'junk-removal',
    h1: 'Junk Removal in Shelbyville, Indiana',
    metaDescription:
      'Junk removal and property cleanouts in Shelbyville, Indiana. Columbus-based, woman-owned, MBE/WBE certified. Free photo-based quotes. (812) 302-6833.',
    intro:
      'Shelbyville junk removal does not have to be a hassle. Ohana Property & Transition Services serves Shelbyville and Shelby County with junk removal, furniture and appliance haul-away, garage cleanouts, debris removal, and full-property cleanouts. We answer the phone, we keep the appointment, and we route usable items to donation before anything heads to disposal.',
    localContext: [
      'Regular Shelbyville and Shelby County service area',
      'Same-week scheduling for most Shelbyville addresses',
      'Donation-conscious recovery through the Hearth Project',
      'MBE and WBE certified, licensed and insured',
      'Photo-based quotes. No in-home estimates required',
    ],
    services: [
      'Junk Removal',
      'Furniture Removal',
      'Appliance Removal',
      'Debris Removal',
      'Garage and Basement Cleanouts',
    ],
    faqs: [
      {
        q: 'Do you serve Shelbyville, Indiana?',
        a: 'Yes. Shelbyville and Shelby County are part of our regular service area. Same scheduling priority as Columbus jobs.',
      },
      {
        q: 'How much is junk removal in Shelbyville, IN?',
        a: 'Most Shelbyville jobs run $100 to $500 depending on volume. Single-item pickups start at $75 to $150. Whole-room or garage cleanouts typically fall between $250 and $700.',
      },
      {
        q: 'How quickly can you come to Shelbyville?',
        a: 'Most Shelbyville junk removal jobs are scheduled within 24 to 48 hours. For urgent same-day requests, call (812) 302-6833.',
      },
      {
        q: 'What items cannot be removed in Shelbyville?',
        a: 'Hazardous waste, paints, solvents, batteries, certain electronics, and biohazard materials are prohibited under Indiana law. We flag those before the job and point you to the appropriate Shelby County or BCSWMD disposal facility.',
      },
      {
        q: 'Are you licensed and insured in Shelbyville?',
        a: 'Yes. Ohana Property & Transition Services LLC is licensed and carries liability insurance for every Shelbyville job. We are also MBE and WBE certified, which matters for institutional and government-vendor work.',
      },
    ],
  },
  {
    slug: 'north-vernon-in-estate-cleanouts',
    city: 'North Vernon',
    state: 'Indiana',
    county: 'Jennings County',
    service: 'Estate Cleanouts',
    serviceSlug: 'estate-cleanouts',
    h1: 'Estate Cleanouts in North Vernon, Indiana',
    metaDescription:
      'Estate cleanouts in North Vernon, Indiana from a Columbus-based, woman-owned property transition company. MBE/WBE certified. Call (812) 302-6833.',
    intro:
      "North Vernon estate cleanouts come at a hard time for most families. Ohana Property & Transition Services drives east from Columbus to serve North Vernon and Jennings County with full estate cleanouts. Sorting, donating, disposing, and handing the property back to family, realtor, or executor broom-clean and ready for the next step. Founded by Olga Ortega, a former social worker who spent a decade helping families through transitions exactly like this one.",
    localContext: [
      'Regular North Vernon and Jennings County route',
      'Coordination with Jennings County disposal and recycling',
      'Donation partners along the US-50 corridor',
      'Bilingual support. English and Spanish',
      'MBE and WBE certified. Referral-ready for North Vernon estate attorneys',
    ],
    services: [
      'Estate Cleanouts',
      'Senior Downsizing',
      'Inherited Property Clearing',
      'Pre-Listing Property Prep',
      'Donation-Conscious Recovery',
    ],
    faqs: [
      {
        q: 'Do you serve North Vernon, Indiana?',
        a: 'Yes. North Vernon and Jennings County are part of our regular service area from our Columbus base. Same 24 to 48 hour scheduling priority as Columbus jobs.',
      },
      {
        q: 'How much does an estate cleanout cost in North Vernon, IN?',
        a: 'North Vernon estate cleanouts typically run $1,000 to $3,000+ depending on home size and volume. Properties with outbuildings or multi-day scope are quoted from photos in advance so the price is locked in.',
      },
      {
        q: 'How fast can you start an estate cleanout in North Vernon?',
        a: 'Most North Vernon estate cleanouts are scheduled within 24 to 48 hours of approval. Probate-coordinated jobs are scheduled to the executor or attorney timeline.',
      },
      {
        q: 'Do you work with North Vernon estate attorneys?',
        a: 'Yes. Ohana is bonded, insured, and MBE/WBE certified, which makes us referral-ready for North Vernon and Jennings County estate attorneys. We provide written scope, photo documentation, and donation receipts on request.',
      },
      {
        q: 'What happens to belongings from a North Vernon estate?',
        a: 'Usable items are routed through the Hearth Project to Indiana nonprofits and families. Everything else is disposed of through licensed channels. Hazardous items are flagged in advance.',
      },
    ],
  },
  {
    slug: 'greensburg-in-property-cleanouts',
    city: 'Greensburg',
    state: 'Indiana',
    county: 'Decatur County',
    service: 'Property Cleanouts',
    serviceSlug: 'property-cleanouts',
    h1: 'Property Cleanouts in Greensburg, Indiana',
    metaDescription:
      'Property cleanouts in Greensburg, Indiana. Estate, rental turnover, and pre-listing prep. Columbus-based, woman-owned, MBE/WBE certified. (812) 302-6833.',
    intro:
      'Greensburg property cleanouts. Full estates, rental turnovers, pre-listing prep. Handled by a Columbus-based, woman-owned team that treats every Greensburg home like a neighbor. Ohana Property & Transition Services serves Greensburg and Decatur County with photo-based quotes, 24 to 48 hour scheduling, and donation-conscious recovery through the Hearth Project.',
    localContext: [
      'Regular Greensburg and Decatur County route along I-74',
      'Coordination with Decatur County disposal',
      'Realtor-ready broom-clean handoff',
      'Donation routing through the Hearth Project',
      'MBE and WBE certified, licensed and insured',
    ],
    services: [
      'Full Property Cleanouts',
      'Estate Cleanouts',
      'Pre-Listing Property Prep',
      'Rental Turnover Cleanouts',
      'Furniture and Appliance Removal',
    ],
    faqs: [
      {
        q: 'Do you serve Greensburg, Indiana?',
        a: 'Yes. Greensburg and Decatur County are part of our regular service area east of Columbus along I-74. Same scheduling priority as our Columbus jobs.',
      },
      {
        q: 'What does a property cleanout cost in Greensburg, IN?',
        a: 'Greensburg property cleanouts typically run $800 to $3,000 depending on home size and contents. We send a photo-based quote before scheduling so the price is confirmed in advance.',
      },
      {
        q: 'What is included in a Greensburg property cleanout?',
        a: 'A full Greensburg property cleanout includes interior, garage, attic, basement, and yard, sorted into keep/donate/dispose, with furniture, appliances, and debris removed, and a final broom-clean finish.',
      },
      {
        q: 'How fast can you start a Greensburg property cleanout?',
        a: 'Most Greensburg cleanouts are scheduled within 24 to 48 hours of quote approval. Multi-day scope is planned in advance from photos.',
      },
      {
        q: 'Where do items go after a Greensburg cleanout?',
        a: 'Usable items are routed through the Hearth Project to Indiana donation partners. Everything else is disposed of through licensed channels. Hazardous and prohibited items are flagged in advance.',
      },
    ],
  },
];

// Audience blocks used on the rebuilt About page (T-005 Section 3).
export const AUDIENCES = [
  {
    title: 'Homeowners',
    challenge: 'Clearing a property before a move, renovation, or sale.',
    approach:
      'Photo-based quotes, 24 to 48 hour scheduling, and a broom-clean finish so the next step (listing, contractor, or move-in) happens on schedule.',
  },
  {
    title: 'Families',
    challenge: 'Clearing a parent\u2019s home after loss or hospitalization.',
    approach:
      'A pace that respects grief, a sort that respects heirlooms, and a crew that has been trained on how to behave inside a home that means something.',
  },
  {
    title: 'Seniors',
    challenge: 'Downsizing, emotionally and physically.',
    approach:
      'Patience, no judgment, and the option to walk away mid-job and come back. We move at the speed our senior clients ask us to move at.',
  },
  {
    title: 'Estate Executors',
    challenge: 'Legal timelines, accounting requirements, and probate coordination.',
    approach:
      'Bonded and insured, written scope, photo documentation, donation receipts, and disposal pathway documentation for estate accounting.',
  },
  {
    title: 'Realtors',
    challenge: 'Pre-listing cleanouts where vendor reliability impacts the listing timeline.',
    approach:
      'Confirmed scheduling, on-time arrival, broom-clean handoff, and direct communication with the seller or family so the listing agent does not get pulled into logistics.',
  },
  {
    title: 'Property Managers',
    challenge: 'Tenant turnovers that need fast, repeat-friendly pricing.',
    approach:
      'Consistent per-unit pricing, scheduling that lines up with leasing cycles, and a relationship that gets stronger with every turnover.',
  },
  {
    title: 'Real Estate Investors',
    challenge: 'Foreclosure and distressed-property clearing on acquisition timelines.',
    approach:
      'Fast scope, fast scheduling, and a finish that lets the contractor walk in and start work. We work to your closing date, not ours.',
  },
  {
    title: 'Estate Attorneys',
    challenge: 'Finding a bonded, insured, MBE/WBE certified vendor for client referrals.',
    approach:
      'Documentation-ready, certified, and discreet. We are referral-ready for Indiana estate attorneys and provide everything probate accounting needs.',
  },
];
